/**
 * 每日护理看板组件 - 稳定布局
 * 优化间距，固定高度区域，减少布局抖动
 */

import React, { useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Calendar, Loader2, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";

import { DaySlider } from "./DaySlider";
import { TaskCard } from "./TaskCard";
import { CompletionFeedback } from "./CompletionFeedback";
import { buildDailyDashboard } from "@/lib/care-plan-adapter";
import type { DailyScheduleDay } from "@/types/care-plan";
import type { TaskGroup } from "@/types/daily-dashboard";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

interface DailyCareBoardProps {
  schedule: DailyScheduleDay[];
  currentDay: number;
  completedTaskIds: Set<string>;
  onTaskToggle: (taskId: string, completed: boolean) => void;
  isLoading?: boolean;
  className?: string;
}

/**
 * 任务分组组件
 */
function TaskGroupSection({
  group,
  completedTaskIds,
  onTaskToggle,
}: {
  group: TaskGroup;
  completedTaskIds: Set<string>;
  onTaskToggle: (taskId: string, completed: boolean) => void;
}) {
  return (
    <div className="space-y-2.5">
      {/* 分组标题 */}
      <h3 className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground/50 px-1">
        {group.title}
      </h3>

      {/* 任务列表 */}
      <div className="space-y-2">
        {group.tasks.map((task) => (
          <TaskCard
            key={task.id}
            task={{
              ...task,
              isCompleted: task.isCompleted || completedTaskIds.has(task.id),
            }}
            onToggle={onTaskToggle}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * 紧急指南 - Sheet 弹出式
 */
function EmergencyGuideLink({ className }: { className?: string }) {
  const { t } = useTranslation("assessments");

  const tips = [
    t("carePlan.emergencyTip1"),
    t("carePlan.emergencyTip2"),
    t("carePlan.emergencyTip3"),
    t("carePlan.emergencyTip4"),
  ];

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "text-destructive/80 hover:text-destructive hover:bg-destructive/5",
            "font-medium gap-1.5",
            className,
          )}
        >
          <AlertTriangle className="w-3.5 h-3.5" />
          <span className="text-xs">
            {t("carePlan.emergencyGuide", "Emergency Guide")}
          </span>
        </Button>
      </DialogTrigger>

      <DialogContent className="max-w-2xl max-h-[65vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2.5 text-base">
            <div className="w-7 h-7 rounded-lg bg-destructive/10 flex items-center justify-center">
              <AlertTriangle className="h-4 w-4 text-destructive" />
            </div>
            {t("carePlan.emergencyTips", "紧急情况处理指南")}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {t("carePlan.emergencyGuideDesc", "当出现以下情况时的应对措施")}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-5 space-y-2.5">
          {tips.map((tip, index) => (
            <div
              key={index}
              className="flex gap-3 p-3.5 rounded-xl border bg-card/50"
            >
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center text-xs font-bold">
                {index + 1}
              </div>
              <p className="text-sm text-foreground/90 flex-1 leading-relaxed">
                {tip}
              </p>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function DailyCareBoard({
  schedule,
  currentDay,
  completedTaskIds,
  onTaskToggle,
  isLoading = false,
  className,
}: DailyCareBoardProps) {
  const { t } = useTranslation("assessments");
  const [selectedDayIndex, setSelectedDayIndex] = useState(currentDay);

  // 构建看板数据
  const dashboard = useMemo(
    () =>
      buildDailyDashboard(
        schedule,
        currentDay,
        selectedDayIndex,
        completedTaskIds,
      ),
    [schedule, currentDay, selectedDayIndex, completedTaskIds],
  );

  // 日期选择处理
  const handleDaySelect = useCallback((dayIndex: number) => {
    setSelectedDayIndex(dayIndex);
  }, []);

  // 计算任务统计
  const totalTasks = useMemo(
    () =>
      dashboard.todayTasks.groups.reduce((sum, g) => sum + g.tasks.length, 0),
    [dashboard.todayTasks.groups],
  );

  const completedCount = useMemo(
    () =>
      dashboard.todayTasks.groups.reduce(
        (sum, g) =>
          sum +
          g.tasks.filter((t) => t.isCompleted || completedTaskIds.has(t.id))
            .length,
        0,
      ),
    [dashboard.todayTasks.groups, completedTaskIds],
  );

  if (isLoading) {
    return (
      <div className={cn("flex items-center justify-center py-20", className)}>
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (schedule.length === 0) {
    return (
      <Card className={cn("border-dashed", className)}>
        <CardContent className="flex flex-col items-center justify-center py-16 text-center">
          <Calendar className="h-10 w-10 text-muted-foreground/40 mb-3" />
          <p className="text-sm text-muted-foreground">
            {t("dailyDashboard.noSchedule", "暂无护理计划")}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className={cn("relative", className)}>
      {/* 微光背景 */}
      <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[500px] h-[400px] bg-primary/[0.02] rounded-full blur-3xl pointer-events-none" />

      {/* 主卡片 */}
      <Card
        className={cn(
          "border shadow-xl overflow-hidden relative",
          "bg-card/95 backdrop-blur-lg",
          // 内描边
          "ring-1 ring-inset ring-white/[0.05]",
        )}
      >
        {/* A. 时间轴 */}
        <DaySlider
          days={dashboard.navigation.days}
          selectedDayIndex={selectedDayIndex}
          onDaySelect={handleDaySelect}
        />

        {/* B. 主任务区域 */}
        <CardContent className="p-0">
          <div className="px-5 py-5 space-y-4">
            {/* 标题区域 - 固定高度 */}
            <div className="flex items-center justify-between gap-4 min-h-[40px]">
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2">
                  <h2 className="text-lg font-semibold">
                    {t("dailyDashboard.todayTasks", "Today's Tasks")}
                  </h2>
                  {totalTasks > 0 && (
                    <span className="text-xs text-muted-foreground font-medium tabular-nums">
                      {completedCount}/{totalTasks}
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground/70 mt-0.5 truncate">
                  {dashboard.todayTasks.dayIndex !== undefined
                    ? `${
                        dashboard.todayTasks.titleKey
                          ? // @ts-expect-error titleKey is a dynamic key from backend
                            t(dashboard.todayTasks.titleKey)
                          : dashboard.todayTasks.title
                      }`
                    : dashboard.todayTasks.headerMessage}
                </p>
              </div>

              {/* 紧急指南入口 */}
              <EmergencyGuideLink />
            </div>

            <Separator className="opacity-30" />

            {/* 任务列表 */}
            {dashboard.todayTasks.groups.length > 0 ? (
              <div className="space-y-4">
                {dashboard.todayTasks.groups.map(
                  (group: TaskGroup, index: number) => (
                    <TaskGroupSection
                      key={`${group.title}-${index}`}
                      group={group}
                      completedTaskIds={completedTaskIds}
                      onTaskToggle={onTaskToggle}
                    />
                  ),
                )}
              </div>
            ) : (
              <div className="text-center py-10 border border-dashed rounded-xl bg-muted/5">
                <Calendar className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">
                  {t("dailyDashboard.noTasks", "今日无特定安排，请静养观察")}
                </p>
              </div>
            )}
          </div>
        </CardContent>

        {/* C. 底部反馈 */}
        <CompletionFeedback
          allCompleted={dashboard.todayTasks.allCompleted}
          tomorrowPreview={dashboard.todayTasks.tomorrowPreview}
        />
      </Card>
    </div>
  );
}

export default DailyCareBoard;
